// ZEO-Autopilot (03.11.2026): Befund im Freigabe-Eingang der bestehenden Aufgabenansicht. Alle sieben Entscheidungswege zeigen NUR die
// Aktionsvorschau (welcher bestehende Weg ausgeloest wuerde); ausgefuehrt wird nichts. Befundspeicher nicht verfuegbar -> Karte ausgeblendet.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { betreuungAPI } from '@/api/betreuungAPI';
import { ALLE_ENTSCHEIDUNGEN, ENTSCHEIDUNG, KI_STATUS, angeboteneEntscheidungen, befundZeilen, kiBelegText, vorschauText } from '@/lib/autopilotBefund';

// 04.11.2026: KI-Einzelpruefung nur nach ausdruecklicher Bestaetigung; Ergebnis wird direkt angezeigt (inkl. Zitate, die nicht gespeichert werden).
function KiPruefung({ aufgabeId, verfuegbar, onErgebnis }) {
  const [schritt, setSchritt] = useState('start');
  const [fehler, setFehler] = useState('');
  if (!verfuegbar) return null;
  const starten = () => {
    setSchritt('laeuft'); setFehler('');
    betreuungAPI.autopilotPruefenKi(aufgabeId).then((r) => { setSchritt('start'); onErgebnis(r.befund); })
      .catch((e) => { setSchritt('start'); setFehler(e.message); });
  };
  if (schritt === 'bestaetigen') {
    return (
      <div className="border border-blue-200 bg-blue-50 rounded p-2 space-y-2" data-testid="autopilot-ki-bestaetigen">
        <p>Qwen (Mittwald) wertet die Unterlagen <b>dieser einen Aufgabe und dieses Klienten</b> aus: Bearbeitungsstand, nächste Schritte und bei Bedarf ein Briefentwurf.
          Es wird nichts geändert und nichts versendet. Dauer bis ca. 2 Minuten.</p>
        <div className="flex gap-2">
          <Button size="sm" onClick={starten} data-testid="autopilot-ki-starten">KI-Prüfung starten</Button>
          <Button size="sm" variant="ghost" onClick={() => setSchritt('start')}>Abbrechen</Button>
        </div>
      </div>);
  }
  return (
    <span className="inline-flex items-center gap-2">
      <Button size="sm" variant="outline" disabled={schritt === 'laeuft'} onClick={() => setSchritt('bestaetigen')} data-testid="autopilot-ki-pruefen">
        {schritt === 'laeuft' ? 'KI prüft … (bis ca. 2 Minuten)' : 'Mit KI prüfen (Qwen)'}
      </Button>
      {fehler && <span className="text-red-700" data-testid="autopilot-ki-fehler">{fehler}</span>}
    </span>);
}

function KiAnalyse({ ki }) {
  if (!ki) return null;
  if (ki.text_entfernt) return <p className="text-slate-500" data-testid="autopilot-ki-veraltet">KI-Analyse nicht mehr aktuell – bitte neu prüfen.</p>;
  return (
    <div className="border border-blue-200 rounded p-2 space-y-1" data-testid="autopilot-ki-analyse">
      <div className="text-slate-500">KI-Analyse (Qwen) – Vorschlag, kein Nachweis</div>
      <p><b>Stand:</b> {KI_STATUS[ki.status] || ki.status}{ki.stand ? ` – ${ki.stand}` : ''}</p>
      {(ki.naechste_schritte || []).length > 0 && (
        <div><b>Nächste Schritte:</b><ol className="list-decimal ml-5" data-testid="autopilot-ki-schritte">{ki.naechste_schritte.map((x, i) => <li key={i}>{x}</li>)}</ol></div>)}
      {(ki.fehlende_informationen || []).length > 0 && <p><b>Fehlt:</b> {ki.fehlende_informationen.join(' · ')}</p>}
      <div><b>Belege:</b>{(ki.belege || []).length
        ? <ul className="list-disc ml-5" data-testid="autopilot-ki-belege">{ki.belege.map((x, i) => <li key={i}>{kiBelegText(x)}</li>)}</ul>
        : <span> keine belegten Zitate{(ki.verworfen || []).length ? ` (${ki.verworfen.length} unbelegte Aussage(n) verworfen)` : ''}</span>}
      </div>
    </div>);
}

export default function AutopilotBefundKarte({ aufgabeId }) {
  const [zustand, setZustand] = useState({ art: 'laden' });
  const [vorschau, setVorschau] = useState(null);
  const [pruefe, setPruefe] = useState(false);
  const [kiVerfuegbar, setKiVerfuegbar] = useState(false);
  useEffect(() => { betreuungAPI.autopilotStatus().then((s) => setKiVerfuegbar(Boolean(s?.ki_einzel))).catch(() => setKiVerfuegbar(false)); }, []);
  const kiErgebnis = (b) => { setVorschau(null); setZustand({ art: 'befund', befund: b }); };
  const laden = useCallback(() => betreuungAPI.autopilotBefund(aufgabeId)
    .then((r) => setZustand(r === null ? { art: 'aus' } : r.fehlt ? { art: 'fehlt' } : { art: 'befund', befund: r }))
    .catch(() => setZustand({ art: 'aus' })), [aufgabeId]);
  useEffect(() => { setVorschau(null); laden(); }, [laden]);
  if (zustand.art === 'aus' || zustand.art === 'laden') return null;
  const pruefen = () => { setPruefe(true); betreuungAPI.autopilotPruefen(aufgabeId).then(laden).catch(() => {}).finally(() => setPruefe(false)); };
  const titel = <CardHeader><CardTitle className="text-base">ZEO-Autopilot – Befund (Vorschau, nichts ausgeführt)</CardTitle></CardHeader>;
  if (zustand.art === 'fehlt') {
    return (
      <Card data-testid="autopilot-befund-leer">{titel}
        <CardContent className="text-sm flex items-center gap-3">Noch kein Befund.
          <Button size="sm" variant="outline" disabled={pruefe} onClick={pruefen} data-testid="autopilot-pruefen">Jetzt prüfen (ohne KI)</Button>
          <KiPruefung aufgabeId={aufgabeId} verfuegbar={kiVerfuegbar} onErgebnis={kiErgebnis} />
        </CardContent>
      </Card>);
  }
  const befund = zustand.befund;
  const angeboten = new Set(angeboteneEntscheidungen(befund));
  const ergebnis = befund.arbeitsergebnis || {};
  return (
    <Card data-testid="autopilot-befund">{titel}
      <CardContent className="space-y-3 text-sm">
        <dl className="grid grid-cols-[11rem_1fr] gap-x-3 gap-y-1">
          {befundZeilen(befund).map(([k, v]) => (<Fragment key={k}><dt className="text-slate-500">{k}</dt><dd data-testid={`autopilot-zeile-${k}`}>{v}</dd></Fragment>))}
        </dl>
        <KiAnalyse ki={befund.ki_analyse} />
        <div>
          <div className="text-slate-500 mb-1">Vorbereitetes Arbeitsergebnis</div>
          {ergebnis.art === 'briefentwurf' && ergebnis.text
            ? <pre className="whitespace-pre-wrap bg-slate-50 p-2 rounded max-h-64 overflow-auto" data-testid="autopilot-entwurf">{ergebnis.text}</pre>
            : <p>{ergebnis.art === 'briefentwurf' ? 'Entwurf nicht mehr aktuell – bitte neu prüfen' : (ergebnis.text || '—')}</p>}
          {ergebnis.art === 'briefentwurf' && !ergebnis.empfaenger_belegt && <p className="text-amber-700">Empfänger nicht belegt – Entwurf muss bearbeitet werden.</p>}
          {ergebnis.art === 'briefentwurf' && ergebnis.text && (
            <div className="flex items-center gap-2 mt-1">
              <Button size="sm" variant="outline" data-testid="autopilot-entwurf-kopieren"
                onClick={() => navigator.clipboard?.writeText(ergebnis.text).catch(() => {})}>Text kopieren</Button>
              <span className="text-slate-500">Entwurf – nicht versendet. Weiterbearbeitung nur manuell in der Korrespondenz.</span>
            </div>)}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="autopilot-entscheidungen">
          {ALLE_ENTSCHEIDUNGEN.map((e) => (
            <Button key={e} size="sm" variant={e === befund.empfehlung ? 'default' : 'outline'} className={angeboten.has(e) ? '' : 'opacity-60'}
              data-testid={`autopilot-${e}`} title={angeboten.has(e) ? 'Vorschau anzeigen' : 'Für diesen Befund nicht vorgesehen – Vorschau zeigt den Grund'}
              onClick={() => betreuungAPI.autopilotAktionVorschau(aufgabeId, e).then(setVorschau).catch((x) => setVorschau({ ok: false, grund: x.message }))}>
              {ENTSCHEIDUNG[e]}
            </Button>))}
          <Button size="sm" variant="ghost" disabled={pruefe} onClick={pruefen} data-testid="autopilot-neu-pruefen">Neu prüfen (ohne KI)</Button>
        </div>
        <KiPruefung aufgabeId={aufgabeId} verfuegbar={kiVerfuegbar} onErgebnis={kiErgebnis} />
        {vorschau && <div className="bg-amber-50 border border-amber-200 rounded p-2" data-testid="autopilot-aktionsvorschau">{vorschauText(vorschau)}</div>}
      </CardContent>
    </Card>
  );
}
